import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { MonitorUp, Power, Loader2 } from 'lucide-react';

/**
 * Banner globale lato POS (FASE B): mostra che una sessione di assistenza remota
 * e' attiva (il gestore vede/controlla questo PC) e permette di terminarla subito.
 */
export const RemoteSessionBanner = () => {
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [ending, setEnding] = useState(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get('/remote/support/session/active');
      setSession(data?.active ? data.session : null);
    } catch (e) { /* silenzioso */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [poll]);

  const end = async () => {
    if (!session) return;
    setEnding(true);
    try {
      await api.post(`/remote/support/session/${session.id}/end`);
      setSession(null);
      toast.success(t('remote.sessionEnded', { defaultValue: 'Sessione di assistenza terminata' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('remote.error', { defaultValue: 'Errore' }));
    } finally { setEnding(false); }
  };

  if (!session) return null;

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[250] flex items-center gap-3 rounded-full border border-primary/40 bg-card/95 backdrop-blur-md px-4 py-2 shadow-lg-token text-sm"
      role="status"
      data-testid="remote-session-banner"
    >
      <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
        <MonitorUp className="h-4 w-4" />
        <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />
      </span>
      <div className="min-w-0">
        <div className="font-semibold leading-tight">{t('remote.sessionActive', { defaultValue: 'Assistenza remota attiva' })}</div>
        <div className="text-xs text-muted-foreground truncate" data-testid="remote-session-banner-manager">
          {session.manager_name || t('remote.manager', { defaultValue: 'Gestore' })}
          {session.agent_connected
            ? ` · ${t('remote.streaming', { defaultValue: 'schermo condiviso' })}`
            : ` · ${t('remote.agentOffline', { defaultValue: 'modulo remoto non avviato su questo PC' })}`}
        </div>
      </div>
      <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-600 shrink-0" onClick={end} disabled={ending} data-testid="remote-session-banner-end">
        {ending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Power className="h-4 w-4 mr-1" />}
        {t('remote.endSession', { defaultValue: 'Termina' })}
      </Button>
    </div>
  );
};
